import {Button} from '@/components/ui/button/Button'
import {DialogDismiss, DialogFooter} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AgendaEventGeneric} from '@/features/agenda/types'
import {useTranslation} from '@/translations/i18n'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {usePermissions} from '@/features/auth/store/store'
import {DeleteGenericEventConfirmationDialog} from '@/features/agenda/components/delete-agenda-event-confirmation-dialog/DeleteGenericEventConfirmationDialog'
import {EditGenericEventFormDialog} from '@/features/agenda/components/edit-generic-event-form-dialog/EditGenericEventFormDialog'
import {EditGenericEventFormValidationSchema} from '@/features/agenda/formSchemas'
import {useState} from 'react'
import dayjs from 'dayjs'

export const AgendaGenericEventDetailsDialogFooter = ({agendaEvent}: {agendaEvent: AgendaEventGeneric}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const dialog = useDialogContext()
    const deleteDialog = useDialogStore()
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

    const isDisabled =
        !permissions?.agenda.write ||
        agendaEvent.status == 'completed' ||
        agendaEvent.status == 'withdrawn' ||
        agendaEvent.status == 'missed' ||
        agendaEvent.status == 'cancelled'

    const editDefaultValues: EditGenericEventFormValidationSchema = {
        id: agendaEvent.id,
        type: 'generic_event',
        note: agendaEvent.note ?? '',
        status: agendaEvent.status,
        consultants:
            agendaEvent.eventConsultants?.map(ec => ({
                id: ec.userId,
                fullName: ec.user.fullName,
                specializationName: ec.user.specialization
            })) ?? [],
        date: {
            date: dayjs(agendaEvent.date).toDate(),
            time: agendaEvent.time?.slice(0, 5) ?? ''
        },
        duration: agendaEvent.duration?.toString() ?? '15'
    }

    const handleOpenEditDialog = () => {
        setIsEditDialogOpen(true)
    }

    const handleEditDialogClose = (open: boolean) => {
        setIsEditDialogOpen(open)
    }

    return (
        <DialogFooter>
            <DeleteGenericEventConfirmationDialog
                event={agendaEvent}
                dialogStore={deleteDialog}
                onDeletionSuccess={() => dialog?.hide()}
            />
            {permissions?.agenda.write && !isDisabled && (
                <>
                    <Button variant="ghostDanger" onClick={() => deleteDialog.show()} shape="rectangle" size="sm">
                        <Trash01Icon size={16} />
                        {t('commons:delete')}
                    </Button>
                </>
            )}
            <Flexbox gap={2}>
                <DialogDismiss render={<Button variant="secondary" size="sm" />}>{t('commons:cancel')}</DialogDismiss>
                {permissions?.agenda.write && !isDisabled && (
                    <Button variant="primaryNeutral" size="sm" onClick={handleOpenEditDialog}>
                        {t('commons:edit')}
                    </Button>
                )}
            </Flexbox>
            {isEditDialogOpen && (
                <EditGenericEventFormDialog
                    defaultValues={editDefaultValues}
                    isOpen={isEditDialogOpen}
                    setIsOpen={handleEditDialogClose}
                />
            )}
        </DialogFooter>
    )
}
